import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { CreditFooter } from "@/components/CreditFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { CreditCost } from "@/components/CreditCost";
import { FieldHint } from "@/components/FieldHint";
import {
  TrendingUp, Coins, Copy, Check, Zap, FileText, ArrowRight, ArrowLeft,
  Loader2, Rocket, Sparkles, Clock, FolderPlus, Link2,
} from "lucide-react";
import { toast } from "sonner";

const GOALS = [
  { id: "first_100", label: "Get my first £100 online" },
  { id: "grow_audience", label: "Grow my audience" },
  { id: "sell_product", label: "Sell a digital product" },
  { id: "get_leads", label: "Get leads for my business" },
  { id: "promote_affiliate", label: "Promote an affiliate product" },
  { id: "other", label: "Something else" },
];

const PLATFORMS = ["TikTok", "Instagram Reels", "YouTube Shorts", "LinkedIn", "Other"];

function CopyBtn({ text, id }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); }
    catch {
      const ta = document.createElement("textarea");
      ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.select(); document.execCommand("copy"); ta.remove();
    }
    setDone(true); toast.success("Copied!"); setTimeout(() => setDone(false), 1500);
  };
  return (
    <button onClick={copy} data-testid={`copy-${id}`} title="Copy"
      className="shrink-0 rounded-full border border-border p-2 text-muted-foreground transition-colors hover:bg-secondary hover:text-primary">
      {done ? <Check size={15} className="text-accent" /> : <Copy size={15} />}
    </button>
  );
}

export default function IncomeMode() {
  const navigate = useNavigate();
  const { refreshCredits } = useAuth();
  const [step, setStep] = useState(0);
  const [goal, setGoal] = useState("");
  const [goalLabel, setGoalLabel] = useState("");
  const [customGoal, setCustomGoal] = useState("");
  const [audience, setAudience] = useState("");
  const [platform, setPlatform] = useState("");
  const [affiliateUrl, setAffiliateUrl] = useState("");
  const [productManual, setProductManual] = useState("");
  const [showManual, setShowManual] = useState(false);
  const [reading, setReading] = useState(false);
  const [affiliateLink, setAffiliateLink] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    // Prefill audience from the ACTIVE project's Offer Profile.
    api.get("/projects/active").then((r) => {
      const aud = r.data.project?.offer?.audience;
      if (aud) setAudience(aud);
    }).catch(() => {});
  }, []);

  const pickGoal = (g) => {
    setGoal(g.id);
    setGoalLabel(g.id === "other" ? "" : g.label);
  };

  const readPage = async () => {
    if (!affiliateUrl.trim()) return;
    setReading(true);
    try {
      const { data } = await api.post("/income-mode/read-url", { affiliate_url: affiliateUrl.trim() });
      setProductManual(data.product_context || "");
      setShowManual(true);
      toast.success("Page read — review & tweak the details below before generating.");
    } catch (e) {
      setShowManual(true);
      toast.error(e.response?.data?.detail || "Couldn't read that page — describe the product below instead.");
    } finally {
      setReading(false);
    }
  };

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/income-mode/generate", {
        goal,
        goal_label: goal === "other" ? customGoal : goalLabel,
        audience,
        platform,
        affiliate_url: goal === "promote_affiliate" ? affiliateUrl.trim() : "",
        product_context: goal === "promote_affiliate" ? productManual.trim() : "",
        affiliate_link: goal === "promote_affiliate" ? affiliateLink.trim() : "",
      });
      setResult(data);
      refreshCredits();
    } catch (e) {
      if (e.response?.status === 422 && goal === "promote_affiliate") {
        // Page couldn't be read — ask the user to describe the product manually.
        setShowManual(true);
        setStep(0);
        toast.error(e.response?.data?.detail || "We couldn't read that page. Describe the product instead.");
      } else if (e.response?.status !== 402) {
        toast.error(e.response?.data?.detail || "Generation failed. Try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const reset = () => { setResult(null); setStep(0); };

  const saveToCampaign = (v) => {
    const s = v.script;
    const fullScript =
      `Hook: ${v.hook}\n\nOpening: ${s.opening}\n\nValue middle: ${s.middle}\n\nPayoff: ${s.payoff}\n\nCTA: ${s.cta}`;
    sessionStorage.setItem(
      "h2i_campaign_prefill",
      JSON.stringify({ angle: v.hook, source_material: fullScript })
    );
    toast.success("Hook & script added — build your course from it");
    navigate("/campaigns/new");
  };

  const canNext =
    (step === 0 && goal && (goal !== "other" || customGoal.trim())
      && (goal !== "promote_affiliate" || affiliateUrl.trim() || productManual.trim())) ||
    (step === 1 && audience.trim()) ||
    (step === 2 && platform);

  // ---- Results screen ----
  if (result) {
    const { variations, next_step } = result;
    const badgeClass = {
      curiosity: "bg-blue-100 text-blue-700",
      fomo: "bg-amber-100 text-amber-700",
      aspiration: "bg-emerald-100 text-emerald-700",
    };
    return (
      <div className="mx-auto max-w-3xl space-y-6" data-testid="income-results">
        <div className="fade-up flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <TrendingUp size={22} />
            </div>
            <div>
              <h1 className="font-head text-3xl font-bold">Your Income Pack</h1>
              <p className="text-sm text-muted-foreground">
                {goal === "other" ? customGoal : goalLabel} · {platform}
              </p>
            </div>
          </div>
          <Button variant="outline" onClick={reset} data-testid="income-startover" className="rounded-full">
            Start over
          </Button>
        </div>

        <p className="text-sm text-muted-foreground">
          Three angles, three <span className="font-medium text-foreground">different emotional triggers</span> — each with a full script that keeps that tone throughout. Pick the one that fits your moment.
        </p>

        {variations.map((v, i) => {
          const s = v.script;
          const fullScript = `${s.opening}\n\n${s.middle}\n\n${s.payoff}\n\n${s.cta}`;
          return (
            <Card key={i} className="border-border p-6" data-testid={`income-variation-${i}`}>
              {/* Emotional-angle label + hook */}
              <div className="mb-3 flex items-center justify-between gap-3">
                <span data-testid={`income-trigger-${v.trigger}`}
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${badgeClass[v.trigger] || "bg-secondary text-secondary-foreground"}`}>
                  <Sparkles size={12} /> {v.label} angle
                </span>
                <CopyBtn id={`hook-${i}`} text={v.hook} />
              </div>
              <p className="rounded-xl border border-primary/20 bg-primary/5 p-3 font-medium" data-testid={`income-hook-line-${i}`}>
                {v.hook}
              </p>

              {/* Script in the same emotional tone */}
              <div className="mt-4 flex items-center justify-between">
                <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  <FileText size={14} className="text-primary" /> Script ({v.label.toLowerCase()} tone)
                </span>
                <CopyBtn id={`script-${i}`} text={fullScript} />
              </div>
              <div className="mt-3 space-y-3 text-sm">
                {[["Opening", s.opening], ["Value middle", s.middle],
                  ["Payoff", s.payoff], ["CTA", s.cta]].map(([label, val]) => (
                  <div key={label}>
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
                    <p className="mt-0.5 leading-relaxed">{val}</p>
                  </div>
                ))}
              </div>

              {/* Bridge into the course builder */}
              <div className="mt-5 border-t border-border pt-4">
                <div className="flex flex-wrap items-center gap-2">
                  <Button variant="outline" onClick={() => saveToCampaign(v)}
                    data-testid={`income-save-to-campaign-${i}`}
                    className="rounded-full border-primary/40 text-primary hover:bg-primary/5">
                    <FolderPlus size={16} className="mr-1.5" /> Save to Campaign
                  </Button>
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                  Save it to a campaign to keep building your course and content.
                </p>
              </div>
            </Card>
          );
        })}

        {/* Next step */}
        <Card className="flex flex-col gap-3 border-primary/30 bg-primary/5 p-6 sm:flex-row sm:items-center sm:justify-between"
          data-testid="income-nextstep-card">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-primary">
              <Rocket size={20} />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Recommended next step</p>
              <p className="font-head text-lg font-bold">{next_step.title}</p>
              <p className="text-sm text-muted-foreground">{next_step.desc}</p>
            </div>
          </div>
          {next_step.coming_soon ? (
            <span data-testid="income-nextstep-soon"
              className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-4 py-2 text-sm font-semibold text-muted-foreground">
              <Clock size={15} /> Coming soon
            </span>
          ) : (
            <Button onClick={() => navigate(next_step.route)} data-testid="income-nextstep-btn"
              className="rounded-full bg-primary px-5 hover:bg-primary/90">
              {next_step.title} <ArrowRight size={16} className="ml-1.5" />
            </Button>
          )}
        </Card>
      </div>
    );
  }

  // ---- Loading screen ----
  if (loading) {
    return (
      <div className="mx-auto max-w-xl py-16">
        <Card className="flex flex-col items-center gap-3 border-dashed p-16 text-center" data-testid="income-loading">
          <Loader2 size={30} className="animate-spin text-primary" />
          <p className="font-head text-lg font-semibold">Building your income pack…</p>
          <p className="text-sm text-muted-foreground">Crafting your hook, script, and next move.</p>
        </Card>
      </div>
    );
  }

  // ---- Question flow ----
  return (
    <div className="mx-auto max-w-xl" data-testid="income-mode-page">
      <div className="mb-8 fade-up flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
          <TrendingUp size={24} />
        </div>
        <div>
          <h1 className="font-head text-4xl font-bold">Income Mode</h1>
          <p className="text-muted-foreground">Three questions → a ready-to-post hook, script & next step.</p>
        </div>
      </div>

      <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">Step {step + 1} of 3</p>

      <div className="mt-6">
        {step === 0 && (
          <div className="space-y-3" data-testid="income-step-goal">
            <h2 className="font-head text-2xl font-bold">What are you trying to do?</h2>
            <div className="grid gap-2.5">
              {GOALS.map((g) => (
                <button key={g.id} onClick={() => pickGoal(g)} data-testid={`income-goal-${g.id}`}
                  className={`rounded-xl border p-4 text-left text-sm font-semibold transition-colors ${
                    goal === g.id ? "border-primary bg-primary/5 ring-2 ring-primary" : "border-border hover:border-primary/40"
                  }`}>{g.label}</button>
              ))}
            </div>
            {goal === "other" && (
              <Input autoFocus data-testid="income-goal-custom" value={customGoal}
                onChange={(e) => setCustomGoal(e.target.value)}
                placeholder="Describe your earning goal" className="mt-2 text-base" />
            )}
            {goal === "promote_affiliate" && (
              <div className="mt-3 space-y-3 rounded-xl border border-primary/20 bg-primary/5 p-4"
                data-testid="income-affiliate-block">
                <div>
                  <label className="text-sm font-semibold">Affiliate product page URL
                    <FieldHint testid="hint-affiliate-url" text="The web page of the product you're promoting as an affiliate. We read it to learn what the product does so your content is specific." />
                  </label>
                  <p className="mb-1.5 text-xs text-muted-foreground">
                    Paste the product link and hit Read page — we'll pull the product details so you can tweak them before generating.
                  </p>
                  <div className="flex gap-2">
                    <Input autoFocus type="url" data-testid="income-affiliate-url" value={affiliateUrl}
                      onChange={(e) => setAffiliateUrl(e.target.value)}
                      placeholder="https://…" className="text-base" />
                    <Button type="button" variant="outline" onClick={readPage}
                      disabled={reading || !affiliateUrl.trim()} data-testid="income-affiliate-read"
                      className="shrink-0 rounded-full">
                      {reading ? <Loader2 size={16} className="mr-1.5 animate-spin" /> : <Link2 size={16} className="mr-1.5" />}
                      {reading ? "Reading…" : "Read page"}
                    </Button>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-semibold">Your affiliate link <span className="font-normal text-muted-foreground">(optional)</span>
                    <FieldHint testid="hint-affiliate-link" text="Your unique tracking link for the product. When someone clicks it and buys, you earn a commission. We drop it into every call-to-action." />
                  </label>
                  <p className="mb-1.5 text-xs text-muted-foreground">
                    Paste your tracking link — we'll drop it into every CTA so each post is ready to publish.
                  </p>
                  <Input type="url" data-testid="income-affiliate-link" value={affiliateLink}
                    onChange={(e) => setAffiliateLink(e.target.value)}
                    placeholder="https://your-affiliate-link.com/…" className="text-base" />
                </div>
                {!showManual ? (
                  <button type="button" onClick={() => setShowManual(true)} data-testid="income-affiliate-manual-toggle"
                    className="text-xs font-medium text-primary hover:underline">
                    Can't share a link? Describe the product instead
                  </button>
                ) : (
                  <div data-testid="income-affiliate-manual">
                    <label className="text-sm font-semibold">Product details
                      <FieldHint testid="hint-product-details" text="What the product is and its main benefits — e.g. name, who it helps, and the top 2-3 things it does. We use this to write your hooks and script." />
                    </label>
                    <p className="mb-1.5 text-xs text-muted-foreground">
                      Auto-filled from the page when you hit Read page — edit anything before generating, or write your own.
                    </p>
                    <Textarea data-testid="income-affiliate-manual-input" value={productManual}
                      onChange={(e) => setProductManual(e.target.value)} rows={5}
                      placeholder="e.g. AcmeNotes — an AI note-taking app for students. Benefits: auto-summaries, flashcards, offline mode…" />
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {step === 1 && (
          <div className="space-y-3" data-testid="income-step-audience">
            <h2 className="font-head text-2xl font-bold">Who is your audience?
              <FieldHint testid="hint-audience" side="bottom" text="The specific group of people you want to reach — their situation, goal or struggle. The more specific, the sharper your hooks. e.g. 'new mums wanting a home side-income'." />
            </h2>
            <p className="text-sm text-muted-foreground">
              {audience ? "Pulled from your Offer Profile — edit if needed." : "Describe the people you're creating for."}
            </p>
            <Input autoFocus data-testid="income-audience" value={audience}
              onChange={(e) => setAudience(e.target.value)}
              placeholder="e.g. Busy parents who want to earn online" className="text-base" />
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3" data-testid="income-step-platform">
            <h2 className="font-head text-2xl font-bold">What platform are you creating for?
              <FieldHint testid="hint-platform" side="bottom" text="Where you'll post this content. We tailor the format and length to fit — e.g. short vertical video for TikTok/Reels, or text posts for LinkedIn." />
            </h2>
            <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
              {PLATFORMS.map((p) => (
                <button key={p} onClick={() => setPlatform(p)} data-testid={`income-platform-${p.replace(/\s+/g, "-").toLowerCase()}`}
                  className={`rounded-xl border p-4 text-sm font-semibold transition-colors ${
                    platform === p ? "border-primary bg-primary/5 ring-2 ring-primary" : "border-border hover:border-primary/40"
                  }`}>{p}</button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="mt-8 flex items-center justify-between">
        <Button variant="ghost" onClick={() => setStep((s) => Math.max(0, s - 1))}
          disabled={step === 0} data-testid="income-back">
          <ArrowLeft size={18} className="mr-1" /> Back
        </Button>
        {step < 2 ? (
          <Button onClick={() => setStep((s) => s + 1)} disabled={!canNext} data-testid="income-next"
            className="rounded-full bg-primary px-6 hover:bg-primary/90">
            Continue <ArrowRight size={18} className="ml-1" />
          </Button>
        ) : (
          <Button onClick={generate} disabled={!canNext} data-testid="income-generate"
            className="rounded-full bg-primary px-6 hover:bg-primary/90">
            <Zap size={18} className="mr-1.5" /> Generate<CreditCost n={10} />
          </Button>
        )}
      </div>
      {step === 2 && (
        <p className="mt-3 flex items-center justify-end gap-1 text-xs text-muted-foreground">
          <Coins size={12} /> This full run costs 10 credits.
        </p>
      )}
      <CreditFooter className="mt-8" />
    </div>
  );
}
